import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { JobApplicationProvider } from './contexts/JobApplicationContext';
import { BookmarkProvider } from './contexts/BookmarkContext';
import { AlertProvider } from './contexts/AlertContext';
import { ThemeProvider } from 'next-themes';
import { Toaster } from './components/ui/toaster';
import { AccountLayout } from './components';
import { AppShell } from './components/AppShell';
import { ForceChangePasswordGate } from './components/ForceChangePasswordGate';
import { HireCheckPrompt } from './components/HireCheckPrompt';
import './App.css';

// 頁面組件
import LoginFormPage from './pages/LoginFormPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import EmailVerifyPage from './pages/EmailVerifyPage';
import RegisterPage from './pages/RegisterPage';
import PublicHomePage from './pages/PublicHomePage';
import HomePage from './pages/HomePage';

import HelperDetailPage from './pages/HelperDetailPage';
import HelperProfileFormPage from './pages/HelperProfileFormPage';
import JobPostingListPage from './pages/JobPostingListPage';
import JobPostingFormPage from './pages/JobPostingFormPage';
import JobPostingDetailPage from './pages/JobPostingDetailPage';
import JobPostingRecommendationsPage from './pages/JobPostingRecommendationsPage';
import HelperRecommendedJobsPage from './pages/HelperRecommendedJobsPage';
import HelperSavedJobsPage from './pages/HelperSavedJobsPage';
import VipPlanPage from './pages/VipPlanPage';
import PaymentPage from './pages/PaymentPage';
import PaymentResultPage from './pages/PaymentResultPage';
import SearchCandidatesPage from './pages/SearchCandidatesPage';
import MyCandidatesPage from './pages/MyCandidatesPage';
import InterviewIntentsPage from './pages/InterviewIntentsPage';
import HelperIntentsPage from './pages/HelperIntentsPage';
import MyJobPostingsPage from './pages/MyJobPostingsPage';
import MyApplicationsPage from './pages/MyApplicationsPage';
import OrdersPage from './pages/OrdersPage';
import IntentAcceptPage from './pages/IntentAcceptPage';
import SettingsPage from './pages/SettingsPage';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import NotFoundPage from './pages/NotFoundPage';
import CmsPage from './components/CmsPage';
// ponytail: ApplicationProcessPage, EmploymentConditionsPage, HelperCategoriesPage removed — replaced by /resources/:slug dynamic route
import ProfilePage from './pages/ProfilePage';

// 受保護的路由組件（保存來源頁以便登入後返回）
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return <div style={{ minHeight: '100vh' }} />;
  }
  if (!user) {
    sessionStorage.setItem('returnUrl', location.pathname + location.search);
    return <Navigate to="/login-form" replace />;
  }
  return <ForceChangePasswordGate>{children}</ForceChangePasswordGate>;
};

const ScrollToTop: React.FC = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

const App: React.FC = () => {
  return (
    <ThemeProvider attribute="class" defaultTheme="light" enableSystem storageKey="theme">
      <AuthProvider>
        <JobApplicationProvider>
            <AlertProvider>
                <Toaster />
                <HireCheckPrompt />
                <Router basename="/">
                <BookmarkProvider>
                <div className="App">
                  <ScrollToTop />
                  <Routes>
                    {/* 公開路由 */}
                    <Route element={<AppShell />}>
                      <Route path="/" element={<PublicHomePage />} />
                      <Route path="/login-form" element={<LoginFormPage />} />
                      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                      <Route path="/email-verify" element={<EmailVerifyPage />} />
                      <Route path="/about" element={<AboutPage />} />
                      <Route path="/contact" element={<ContactPage />} />
                      <Route path="/subscription" element={<VipPlanPage />} />
                      <Route path="/helper/:id" element={<HelperDetailPage />} />
                      <Route path="/candidates" element={<SearchCandidatesPage />} />
                      <Route path="/job-postings" element={<JobPostingListPage />} />
                      <Route path="/job-postings/:id/detail" element={<JobPostingDetailPage />} />
                      <Route path="/job-postings/:id/recommendations" element={<JobPostingRecommendationsPage />} />
                      <Route path="/resources/:slug" element={<CmsPage />} />
                      <Route path="/resources/:slug/:childSlug" element={<CmsPage />} />
                      <Route path="/intent/accept/:token" element={<IntentAcceptPage />} />
                    </Route>
                    <Route element={<AppShell />}>
                      <Route path="/register" element={<RegisterPage />} />
                    </Route>

                    {/* 受保護的路由：ProtectedLayout 提供 PageLayout + DesignHeader + Outlet */}
                    <Route path="/*" element={<ProtectedRoute><Outlet /></ProtectedRoute>}>
                      <Route element={<AppShell contained />}>
                        {/* 帳戶頁面：左側菜單 */}
                        <Route element={<AccountLayout />}>
                          <Route path="home" element={<HomePage />} />
                          <Route path="profile" element={<ProfilePage />} />
                          <Route path="payment" element={<PaymentPage />} />
                          <Route path="payment/plans" element={<VipPlanPage isDashboard />} />
                          <Route path="orders" element={<OrdersPage />} />
                          <Route path="settings" element={<SettingsPage />} />
                          <Route path="helper/saved-jobs" element={<HelperSavedJobsPage />} />
                          <Route path="helper/recommended-jobs" element={<HelperRecommendedJobsPage />} />
                          <Route path="my-candidates" element={<MyCandidatesPage />} />
                          <Route path="my-job-postings" element={<MyJobPostingsPage />} />
                          <Route path="my-applications" element={<MyApplicationsPage />} />
                          <Route path="interview-intents" element={<InterviewIntentsPage />} />
                          <Route path="helper/invitations" element={<HelperIntentsPage />} />
                          <Route path="payment/result" element={<PaymentResultPage />} />
                        </Route>
                        <Route path="helper/profile/new" element={<HelperProfileFormPage />} />
                        <Route path="helper/profile/:id/edit" element={<HelperProfileFormPage />} />
                        <Route path="job-postings/new" element={<JobPostingFormPage />} />
                        <Route path="job-postings/:id/edit" element={<JobPostingFormPage />} />
                        <Route path="*" element={<NotFoundPage />} />
                      </Route>
                    </Route>
                    <Route path="*" element={<NotFoundPage />} />
                  </Routes>
                </div>
                </BookmarkProvider>
              </Router>
            </AlertProvider>
        </JobApplicationProvider>
      </AuthProvider>
    </ThemeProvider>
  );
};

export default App;
